import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAreasNav } from '../../contexts/AreasContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  IconAreas,
  IconCamera,
  IconChevronDown,
  IconClose,
  IconDashboard,
  IconLogout,
  IconMoon,
  IconPlus,
  IconShield,
  IconSun,
  IconUser,
  IconWallet,
} from '../icons/Icons';
import type { IconProps } from '../icons/Icons';
import './Sidebar.css';

interface SidebarProps {
  /** Открыто ли меню на мобильных (drawer). */
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

interface NavLinkItem {
  id: string;
  title: string;
  url: string;
  Icon: React.FC<IconProps>;
}

const AREAS_GROUP_KEY = 'flypix:sidebar-areas-open';

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onCloseMobile }) => {
  const { user, isAdmin, logout } = useAuth();
  const { areas } = useAreasNav();
  const { resolvedTheme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [areasOpen, setAreasOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(AREAS_GROUP_KEY) !== 'closed';
    } catch {
      return true;
    }
  });

  // Открываем группу участков, если пользователь находится внутри участка
  useEffect(() => {
    if (location.pathname.startsWith('/area/')) {
      setAreasOpen(true);
    }
  }, [location.pathname]);

  const toggleAreas = () => {
    setAreasOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(AREAS_GROUP_KEY, next ? 'open' : 'closed');
      } catch {
        /* сохранение состояния не критично */
      }
      return next;
    });
  };

  // Разделы, которые идут после группы «Участки»
  const secondaryLinks = useMemo<NavLinkItem[]>(
    () => [
      { id: 'balance', title: 'Баланс', url: '/balance', Icon: IconWallet },
      { id: 'profile', title: 'Профиль', url: '/profile', Icon: IconUser },
    ],
    []
  );

  const handleLogout = async () => {
    onCloseMobile();
    await logout();
    navigate('/login');
  };

  const isAreaActive = location.pathname.startsWith('/area/');

  return (
    <>
      <button
        type="button"
        className={`sidebar-scrim${mobileOpen ? ' sidebar-scrim--visible' : ''}`}
        aria-hidden={!mobileOpen}
        tabIndex={-1}
        onClick={onCloseMobile}
      />

      <aside
        className={`sidebar${mobileOpen ? ' sidebar--open' : ''}`}
        aria-label="Основная навигация"
      >
        <div className="sidebar__shell" aria-hidden="true" />

        <div className="sidebar__top">
          <NavLink to="/dashboard" className="sidebar__logo" onClick={onCloseMobile}>
            <img
              src={`${process.env.PUBLIC_URL}/branding/full-logo-light.svg`}
              alt="Flypix"
              className="sidebar__logo-img sidebar__logo-img--light"
            />
            <img
              src={`${process.env.PUBLIC_URL}/branding/full-logo.svg`}
              alt="Flypix"
              className="sidebar__logo-img sidebar__logo-img--dark"
            />
          </NavLink>

          <button
            type="button"
            className="sidebar__close"
            aria-label="Закрыть меню"
            onClick={onCloseMobile}
          >
            <IconClose size={18} />
          </button>
        </div>

        <nav className="sidebar__nav ui-scroll" aria-label="Разделы">
          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              `sidebar__item${isActive ? ' sidebar__item--active' : ''}`
            }
            onClick={onCloseMobile}
          >
            <IconDashboard size={20} />
            <span className="sidebar__item-label">Дашборд</span>
          </NavLink>

          <div className={`sidebar__group${areasOpen ? ' sidebar__group--open' : ''}`}>
            <button
              type="button"
              className={`sidebar__item sidebar__item--group${
                isAreaActive && !areasOpen ? ' sidebar__item--active' : ''
              }`}
              aria-expanded={areasOpen}
              onClick={toggleAreas}
            >
              <IconAreas size={20} />
              <span className="sidebar__item-label">Участки</span>
              {areas.length > 0 && (
                <span className="sidebar__count">{areas.length}</span>
              )}
              <IconChevronDown size={16} className="sidebar__chevron" />
            </button>

            {areasOpen && (
              <div className="sidebar__sub">
                {areas.length === 0 ? (
                  <NavLink
                    to="/dashboard"
                    className="sidebar__subitem sidebar__subitem--muted"
                    onClick={onCloseMobile}
                  >
                    <IconPlus size={16} />
                    <span className="sidebar__item-label">Создать участок</span>
                  </NavLink>
                ) : (
                  areas.map((area) => (
                    <NavLink
                      key={area.id}
                      to={`/area/${area.id}`}
                      className={({ isActive }) =>
                        `sidebar__subitem${isActive ? ' sidebar__subitem--active' : ''}`
                      }
                      onClick={onCloseMobile}
                      title={area.name}
                    >
                      <IconCamera size={16} />
                      <span className="sidebar__item-label">{area.name}</span>
                      {typeof area.cameras_count === 'number' && (
                        <span className="sidebar__count">{area.cameras_count}</span>
                      )}
                    </NavLink>
                  ))
                )}
              </div>
            )}
          </div>

          {secondaryLinks.map(({ id, title, url, Icon }) => (
            <NavLink
              key={id}
              to={url}
              className={({ isActive }) =>
                `sidebar__item${isActive ? ' sidebar__item--active' : ''}`
              }
              onClick={onCloseMobile}
            >
              <Icon size={20} />
              <span className="sidebar__item-label">{title}</span>
            </NavLink>
          ))}

          {isAdmin && (
            <>
              <div className="sidebar__divider" role="separator" />
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  `sidebar__item${isActive ? ' sidebar__item--active' : ''}`
                }
                onClick={onCloseMobile}
              >
                <IconShield size={20} />
                <span className="sidebar__item-label">Админ-панель</span>
              </NavLink>
            </>
          )}
        </nav>

        <div className="sidebar__footer">
          <button
            type="button"
            className="sidebar__ghost-btn"
            onClick={toggleTheme}
            aria-label={
              resolvedTheme === 'dark' ? 'Включить светлую тему' : 'Включить тёмную тему'
            }
          >
            {resolvedTheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
            <span className="sidebar__item-label">
              {resolvedTheme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
            </span>
          </button>

          <div className="sidebar__user">
            <span className="sidebar__avatar" aria-hidden="true">
              {(user?.name || 'F').trim().charAt(0).toUpperCase()}
            </span>
            <span className="sidebar__user-text">
              <span className="sidebar__user-name">{user?.name || 'Пользователь'}</span>
              <span className="sidebar__user-mail">{user?.email}</span>
            </span>
            <button
              type="button"
              className="sidebar__logout"
              aria-label="Выйти из кабинета"
              title="Выйти"
              onClick={() => void handleLogout()}
            >
              <IconLogout size={18} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
